import apiClient from './client';

// Enums
export enum SupplierStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING = 'PENDING',
}

export enum SupplierType {
  FEED = 'FEED',
  MEDICATION = 'MEDICATION',
  EQUIPMENT = 'EQUIPMENT',
  SUPPLIES = 'SUPPLIES',
  SERVICES = 'SERVICES',
  MIXED = 'MIXED',
}

export enum PaymentTerms {
  NET_30 = 'NET_30',
  NET_60 = 'NET_60',
  COD = 'COD',
  PREPAID = 'PREPAID',
  NET_15 = 'NET_15',
  NET_45 = 'NET_45',
}

// Interfaces
export interface SupplierMetadata {
  tags?: string[];
  notes?: string;
  certifications?: string[];
  qualityRating?: number;
  deliveryRating?: number;
  serviceRating?: number;
  lastOrderDate?: string;
  totalOrders?: number;
  totalSpent?: number;
  preferredDeliveryDays?: string[];
  minimumOrderValue?: number;
  discountTiers?: Array<{
    threshold: number;
    discount: number;
  }>;
  // Payment tracking fields
  paymentHistory?: Array<{
    date: string;
    amount: number;
    paymentMethod: string;
    description: string;
    reference?: string;
  }>;
  lastPaymentDate?: string;
  totalPaid?: number;
}

export interface Supplier {
  id: string;
  name: string;
  code: string;
  type: SupplierType;
  status: SupplierStatus;
  contactPerson?: string;
  email?: string;
  phone?: string;
  alternativePhone?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  website?: string;
  taxNumber?: string;
  registrationNumber?: string;
  paymentTerms: PaymentTerms;
  creditLimit?: number;
  currentBalance: number;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranchCode?: string;
  description?: string;
  metadata?: SupplierMetadata;
  createdAt: Date;
  updatedAt: Date;
  fullAddress?: string;
  isActive: boolean;
  overallRating?: number;
}

export interface CreateSupplierDto {
  name: string;
  code: string;
  type: SupplierType;
  status?: SupplierStatus;
  contactPerson?: string;
  email?: string;
  phone?: string;
  alternativePhone?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  website?: string;
  taxNumber?: string;
  registrationNumber?: string;
  paymentTerms?: PaymentTerms;
  creditLimit?: number;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranchCode?: string;
  description?: string;
  metadata?: SupplierMetadata;
}

export interface UpdateSupplierDto extends Partial<CreateSupplierDto> {}

export interface GetSuppliersDto {
  search?: string;
  type?: SupplierType;
  status?: SupplierStatus;
  city?: string;
  province?: string;
  paymentTerms?: PaymentTerms;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SuppliersResponse {
  suppliers: Supplier[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SupplierStats {
  totalSuppliers: number;
  activeSuppliers: number;
  inactiveSuppliers: number;
  suspendedSuppliers: number;
  pendingSuppliers: number;
  suppliersByType: Record<SupplierType, number>;
  totalOutstandingBalance: number;
  averageRating?: number;
  topSuppliers: Array<{
    id: string;
    name: string;
    totalSpent: number;
    totalOrders: number;
  }>;
}

// API Functions
export const suppliersApi = {
  // Get all suppliers with filtering and pagination
  getSuppliers: async (filters: GetSuppliersDto = {}): Promise<SuppliersResponse> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString ? `suppliers?${queryString}` : 'suppliers';
    const response = await apiClient.get<SuppliersResponse>(url);
    return response.data;
  },

  // Get supplier by ID
  getSupplier: async (id: string): Promise<Supplier> => {
    const response = await apiClient.get<Supplier>(`suppliers/${id}`);
    return response.data;
  },

  // Get supplier by code
  getSupplierByCode: async (code: string): Promise<Supplier> => {
    const response = await apiClient.get<Supplier>(`suppliers/code/${code}`);
    return response.data;
  },

  // Create new supplier
  createSupplier: async (data: CreateSupplierDto): Promise<Supplier> => {
    const response = await apiClient.post<Supplier>('suppliers', data);
    return response.data;
  },

  // Update supplier
  updateSupplier: async (id: string, data: UpdateSupplierDto): Promise<Supplier> => {
    const response = await apiClient.patch<Supplier>(`suppliers/${id}`, data);
    return response.data;
  },

  // Delete supplier
  deleteSupplier: async (id: string): Promise<void> => {
    await apiClient.delete(`suppliers/${id}`);
  },

  // Update supplier balance
  updateSupplierBalance: async (id: string, amount: number): Promise<Supplier> => {
    const response = await apiClient.patch<Supplier>(`suppliers/${id}/balance`, { amount });
    return response.data;
  },

  // Get supplier statistics
  getStatistics: async (): Promise<SupplierStats> => {
    const response = await apiClient.get<SupplierStats>('suppliers/statistics');
    return response.data;
  },

  // Get active suppliers
  getActiveSuppliers: async (): Promise<Supplier[]> => {
    const response = await apiClient.get<Supplier[]>('suppliers/active');
    return response.data;
  },

  // Get suppliers by type
  getSuppliersByType: async (type: SupplierType): Promise<Supplier[]> => {
    const response = await apiClient.get<Supplier[]>(`suppliers/by-type/${type}`);
    return response.data;
  },
};

// Helper functions
export const getSupplierStatusColor = (status: SupplierStatus): string => {
  switch (status) {
    case SupplierStatus.ACTIVE:
      return 'green';
    case SupplierStatus.INACTIVE:
      return 'gray';
    case SupplierStatus.SUSPENDED:
      return 'red';
    case SupplierStatus.PENDING:
      return 'yellow';
    default:
      return 'gray';
  }
};

export const getSupplierTypeColor = (type: SupplierType): string => {
  switch (type) {
    case SupplierType.FEED:
      return 'green';
    case SupplierType.MEDICATION:
      return 'red';
    case SupplierType.EQUIPMENT:
      return 'blue';
    case SupplierType.SUPPLIES:
      return 'purple';
    case SupplierType.SERVICES:
      return 'orange';
    case SupplierType.MIXED:
      return 'gray';
    default:
      return 'gray';
  }
};

export const getPaymentTermsLabel = (terms: PaymentTerms): string => {
  switch (terms) {
    case PaymentTerms.NET_30:
      return 'Net 30 Days';
    case PaymentTerms.NET_60:
      return 'Net 60 Days';
    case PaymentTerms.NET_15:
      return 'Net 15 Days';
    case PaymentTerms.NET_45:
      return 'Net 45 Days';
    case PaymentTerms.COD:
      return 'Cash on Delivery';
    case PaymentTerms.PREPAID:
      return 'Prepaid';
    default:
      return terms;
  }
};

export const formatSupplierType = (type: SupplierType): string => {
  return type.charAt(0) + type.slice(1).toLowerCase();
};

export const formatSupplierStatus = (status: SupplierStatus): string => {
  return status.charAt(0) + status.slice(1).toLowerCase();
}; 